import {
	DealHistoryTab,
	Modal,
	PdfIcon,
	PerformanceTrendChart,
	ProfileHeaderCard,
	RatingDistributionBars,
	SpinnerWithBackground,
	TabBar,
	Text,
	ZipIcon,
} from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAggregationStore } from '../../stores/aggregationStore'
import { type ExportMenuItem, ExportSplitButton } from '../ExportSplitButton'
import { useRatingScaleMax } from '../useRatingScaleMax'
import { PerformanceByQuestion } from './PerformanceByQuestion'

type ModalTab = 'profile' | 'history'

export function ParticipantProfileModal() {
	const navigate = useNavigate()
	const scaleMax = useRatingScaleMax()
	const {
		selectedParticipantId,
		participantProfile,
		loadParticipantProfile,
		loadParticipantPerformanceByQuestion,
		isParticipantProfileLoading,
		clearSelectedParticipant,
		exportParticipantProfilePdf,
		exportDealTeamParticipantsReportsZip,
		isExportingParticipantProfilePdf,
		isExportingDealTeamParticipantsReportsZip,
		participants,
		cycleTemplates,
		participantSelectedTemplateId,
	} = useAggregationStore()

	const [activeTab, setActiveTab] = useState<ModalTab>('profile')

	useEffect(() => {
		if (selectedParticipantId) {
			loadParticipantProfile(selectedParticipantId)
		}
	}, [selectedParticipantId, loadParticipantProfile])

	useEffect(() => {
		if (!selectedParticipantId) return
		// Initial-load only. Subsequent template changes go through selectParticipantTemplate
		// which calls loadParticipantPerformanceByQuestion directly. Without this guard the
		// effect double-fires because loadParticipantPerformanceByQuestion synchronously sets
		// participantSelectedTemplateId — which is in this effect's deps.
		if (participantSelectedTemplateId !== null) return
		const desiredTemplateId = cycleTemplates[0]?.templateId
		if (!desiredTemplateId) return
		loadParticipantPerformanceByQuestion(selectedParticipantId, desiredTemplateId)
	}, [selectedParticipantId, cycleTemplates, participantSelectedTemplateId, loadParticipantPerformanceByQuestion])

	useEffect(() => {
		if (!selectedParticipantId) {
			setActiveTab('profile')
		}
	}, [selectedParticipantId])

	const selectedParticipantSummary = participants?.data.find(
		(participant) => participant.userId === selectedParticipantId,
	)
	const participantName = participantProfile
		? [participantProfile.firstName, participantProfile.lastName].filter(Boolean).join(' ')
		: selectedParticipantSummary
			? [selectedParticipantSummary.firstName, selectedParticipantSummary.lastName].filter(Boolean).join(' ')
			: ''

	const title = participantName ? `View ${participantName} DealTeam Profile` : 'Participant Profile'

	const exportItems: ExportMenuItem[] = selectedParticipantId
		? [
				{
					key: 'profile-pdf',
					label: 'Export profile (.pdf)',
					ariaLabel: 'Export profile (.pdf)',
					icon: <PdfIcon size={20} className='fill-neutral-500' />,
					action: () => exportParticipantProfilePdf(selectedParticipantId),
					busy: isExportingParticipantProfilePdf,
					disabled: !participantProfile || isParticipantProfileLoading,
				},
				{
					key: 'reports-zip',
					label: 'Export all reports (.zip)',
					ariaLabel: 'Export all reports (.zip)',
					icon: <ZipIcon size={20} className='fill-neutral-500' />,
					action: () => exportDealTeamParticipantsReportsZip({ userIds: [selectedParticipantId] }),
					busy: isExportingDealTeamParticipantsReportsZip,
					disabled: !participantProfile || isParticipantProfileLoading,
				},
			]
		: []

	return (
		<Modal
			isOpen={!!selectedParticipantId}
			onClose={clearSelectedParticipant}
			title={title}
			className='!w-[min(95vw,1400px)] !max-w-none mx-0'
			buttons={[{ children: 'Close', variant: 'secondary', onClick: clearSelectedParticipant }]}>
			<div className='flex flex-col gap-6 -m-6 p-0'>
				<div className='flex items-center justify-between gap-4 px-6 pt-2'>
					<TabBar
						tabs={[
							{ id: 'profile', label: 'DealTeam Profile' },
							{ id: 'history', label: 'Deal History' },
						]}
						activeTab={activeTab}
						onChange={(tab) => setActiveTab(tab as ModalTab)}
						aria-label='Participant profile sections'
					/>
					{selectedParticipantId && <ExportSplitButton items={exportItems} />}
				</div>

				<div className='px-6 pb-6 flex flex-col gap-6'>
					{isParticipantProfileLoading && (
						<div className='relative min-h-96'>
							<SpinnerWithBackground />
						</div>
					)}

					{!isParticipantProfileLoading && !participantProfile && (
						<div className='py-12 text-center text-neutral-500'>
							<Text variant='small'>No participant profile data available.</Text>
						</div>
					)}

					{!isParticipantProfileLoading && participantProfile && activeTab === 'profile' && (
						<>
							<ProfileHeaderCard profile={participantProfile} scaleMax={scaleMax} />
							<PerformanceTrendChart trend={participantProfile.performanceTrend} scaleMax={scaleMax} />
							<RatingDistributionBars distribution={participantProfile.ratingDistribution} />
							<PerformanceByQuestion />
						</>
					)}

					{!isParticipantProfileLoading && participantProfile && activeTab === 'history' && (
						<DealHistoryTab
							profile={participantProfile}
							scaleMax={scaleMax}
							onViewReport={(cycleId) => {
								if (!selectedParticipantId) return
								clearSelectedParticipant()
								navigate(`/dealteam/deals/${cycleId}/participants/${selectedParticipantId}/report`)
							}}
						/>
					)}
				</div>
			</div>
		</Modal>
	)
}
